import type { Config } from '@wagmi/core';
import { getBalance, readContract, switchChain } from '@wagmi/core';
import type { Address, Hex } from 'viem';
import { erc20Abi, parseUnits } from 'viem';
import type { EvmEventEmitterInstance } from '~/service/utils/eventEmitter';
import type { ConfigsProvider } from '~/service/config';
import type { EvmChainId, EvmNetwork } from '~/service/EVM/config/types';

/**
 @param config - web3 provider config
 */

export interface EvmProviderInterface {
    config: Config;
    eventEmitter: EvmEventEmitterInstance;
    chainMap: ConfigsProvider['evm']['map']['chainMap'];
}

export default class EvmProvider implements EvmProviderInterface {
    public config;
    public eventEmitter;
    public chainMap: ConfigsProvider['evm']['map']['chainMap'];

    constructor({ config, eventEmitter, chainMap }: EvmProviderInterface) {
        this.config = config;
        this.eventEmitter = eventEmitter;
        this.chainMap = chainMap;
    }

    async getUserBalance({ userAddress, erc20TokenAddress, chainId }: {
        userAddress: Address;
        chainId: User['chainId'];
        erc20TokenAddress?: Address;
    }) {
        if (!chainId || !userAddress) {
            throw new Error('Unable to get user balance');
        }
        try {
            if (!erc20TokenAddress) {
                const { value } = await getBalance(
                    this.config,
                    {
                        chainId,
                        address: userAddress
                    }
                );
                return value;
            }
            else {
                return await readContract(this.config, {
                    address: erc20TokenAddress,
                    abi: erc20Abi,
                    functionName: 'balanceOf',
                    args: [userAddress],
                    chainId
                });
            }
        }
        catch (err) {
            throw new Error('Unable to get user balance');
        }
    }

    public generateSalt() {
        const randomBytes = crypto.getRandomValues(new Uint8Array(32));
        return ('0x' + Array.from(randomBytes)
            .map(b => b.toString(16).padStart(2, '0'))
            .join('')) as Hex;
    }

    public getChainByNetwork(network: EvmNetwork) {
        const target = this.chainMap[network];
        if (!target) throw new Error('Unsupported network');
        return target;
    }

    public async calcValueByErc20Contract({ address, network, value }: { address: Address; network: EvmNetwork; value: number }) {
        try {
            const decimals = await readContract(this.config, {
                address,
                chainId: this.chainMap[network].id,
                functionName: 'decimals',
                abi: erc20Abi
            });

            return parseUnits(
                String(value),
                decimals
            );
        }
        catch (err) {
            throw new Error('Value calculation error');
        }
    }

    public async ensureCorrectNetwork({ currentChainId, targetNetwork }: { targetNetwork: EvmNetwork; currentChainId?: EvmChainId }) {
        const target = this.getChainByNetwork(targetNetwork);

        if (currentChainId === target.id) return;

        try {
            await switchChain(this.config, { chainId: target.id });
        }
        catch (error) {
            throw new Error(`Failed to switch network to "${target.id}" (${targetNetwork})`);
        }
    }
}
